import React, { useState, useEffect } from 'react';
// Assuming PlayFab service is here
import { PlayFab } from './services/PlayFab'; // Adjusted path

// Define interface for the expected structure of blogData
interface BlogData {
  id: string;
  title: string;
  difficulty: string;
  theme: string;
  concept_focus: string;
  narrative: {
    intro: string;
    body: string;
    mnemonic?: string;
    conclusion: string;
  };
  scenario: {
    game_type: string;
    positions: { hero: string; villain: string };
    stacks: { hero: number; villain: number };
    preflop: { hero_hand: string[]; villain_action: string; hero_action: string };
    flop?: { board: string[]; actions: { player: string; action: string }[] };
    turn?: { card: string; actions: { player: string; action: string }[] };
    river?: { card: string; actions: { player: string; action: string }[] };
  };
  gto_reasoning?: { points: string[] };
  math_simplified?: { villain_bet: string; fold_equity_needed: string; note: string };
  bluff_criteria?: { has_equity: boolean; has_blockers: boolean; can_fold_to_shove: boolean };
  vs_population_advice?: { tight_opponent: string; bluffy_opponent: string };
  takeaway?: string;
}

// Define interface for CloudScript result structure
interface PlayFabCloudScriptResult {
    success: boolean;
    data?: {
        FunctionResult?: {
            FunctionResult?: { // The actual nested result
                success: boolean;
                isPremium?: boolean;
                content?: string;
                error?: string;
                details?: string;
            };
        };
        // Other potential fields from PlayFab response
        APIRequestsIssued?: number;
        ExecutionTimeSeconds?: number;
    };
    error?: {
      message?: string;
    };
    errorMessage?: string;
}

// Add types for state variables
function LearnPage(): JSX.Element {
  const [blogData, setBlogData] = useState<BlogData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isPremium, setIsPremium] = useState<boolean>(true); 
  
  // State for day selection
  const [currentDayUTC, setCurrentDayUTC] = useState<number>(new Date().getUTCDay()); // 0 = Sunday, ..., 6 = Saturday
  const [selectedDay, setSelectedDay] = useState<number>(currentDayUTC); // Default to current day
  const [unlockTimeRemaining, setUnlockTimeRemaining] = useState<string | null>(null); // For future days

  // Helper to calculate time until next midnight UTC for a future day
  const calculateUnlockTime = (targetDayUTC: number) => {
    const now = new Date();
    const currentDay = now.getUTCDay();
    let daysUntilTarget = targetDayUTC - currentDay;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Target day is next week
    }
    
    const targetDate = new Date(now);
    targetDate.setUTCDate(now.getUTCDate() + daysUntilTarget);
    targetDate.setUTCHours(0, 0, 0, 0); // Midnight UTC on the target day

    const timeDiff = targetDate.getTime() - now.getTime();
    if (timeDiff <= 0) return null; // Should not happen if target is future, but safety check

    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `Unlocks in ${hours}h ${minutes}m`;
  };

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        setLoading(true);
        setError(null);
        setUnlockTimeRemaining(null); // Reset unlock message
        setBlogData(null); // Clear previous data

        const todayUTC = new Date().getUTCDay();
        setCurrentDayUTC(todayUTC); // Update current day just in case

        // Check if selected day is in the future (relative to UTC day)
        let daysDifference = selectedDay - todayUTC;
        // Handle week wrap-around (e.g., selecting Sun when it's Sat)
        if (daysDifference < 0) daysDifference += 7;
        const isFutureDay = daysDifference > 0 && daysDifference < 7; // It's a future day this week

        if (isFutureDay) {
          console.log(`Selected day ${selectedDay} is in the future.`);
          setUnlockTimeRemaining(calculateUnlockTime(selectedDay));
          setLoading(false);
          return; // Don't fetch
        }

        // Fetching for current or past day
        const titleDataKey = `daily_training_${selectedDay}`;
        console.log(`Fetching data for selected day ${selectedDay} using key: ${titleDataKey}`);

        const result: PlayFabCloudScriptResult = await PlayFab.executeCloudScript({
          FunctionName: 'getPremiumContent',
          FunctionParameter: { titleDataKey: titleDataKey } // Use dynamic key
        });

        console.log(`CloudScript getPremiumContent result for key ${titleDataKey}:`, result);

        const innerFunctionResult = result?.data?.FunctionResult?.FunctionResult;

        if (result?.success && innerFunctionResult?.success) {
          if (innerFunctionResult.content) {
            try {
              setBlogData(JSON.parse(innerFunctionResult.content) as BlogData);
              setIsPremium(true); 
            } catch (parseError) {
              console.error("Error parsing blog content JSON:", parseError);
              setError('Failed to parse blog content.');
            }
          } else {
             // If content is missing for a past/present day, show specific error
             setError(innerFunctionResult.error || 'Content not found for this day.'); 
          }
        } else {
          const errorMsg = innerFunctionResult?.error || result?.error?.message || result?.errorMessage || 'Failed to load blog content.';
          setError(errorMsg);
          if (innerFunctionResult?.isPremium === false) {
            setIsPremium(false);
          }
        }
      } catch (err) {
        console.error("Error calling getPremiumContent CloudScript:", err);
        setError('An error occurred while fetching blog data.');
      } finally {
        setLoading(false);
      }
    };

    if (PlayFab.hasValidSession()) { 
        fetchBlogData();
    } else {
        console.warn("PlayFab session not valid, cannot fetch blog data.");
        setError("Please log in to view premium content.");
        setLoading(false);
        setIsPremium(false); 
    }
  // Depend on selectedDay to refetch when the user clicks a day selector
  }, [selectedDay]); 

  // Define styles object type
  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      maxWidth: '800px',
      margin: '2rem auto',
      padding: '2rem 3rem',
      backgroundColor: 'var(--panel-color, #1a2a4a)',
      border: '1px solid var(--border-color, #2c3e50)',
      color: 'var(--text-color, #e9ecef)',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      fontFamily: 'Verdana, Geneva, sans-serif', 
      lineHeight: '1.8',
      fontSize: '16px',
    },
    mainTitle: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      marginBottom: '0.75rem',
      textAlign: 'center',
      color: 'var(--header-color, #ffffff)',
      lineHeight: '1.3',
    },
    metaInfo: {
      textAlign: 'center',
      color: 'var(--subtext-color, #adb5bd)',
      marginBottom: '2.5rem',
      fontSize: '0.95rem',
    },
    sectionTitle: {
      fontSize: '1.6rem',
      fontWeight: '600',
      marginTop: '2.5rem',
      marginBottom: '1.25rem',
      paddingBottom: '0.6rem',
      borderBottom: '1px solid var(--border-color, #2c3e50)',
      color: 'var(--header-color, #ffffff)',
    },
    subSectionTitle: { 
      fontSize: '1.25rem',
      fontWeight: '600',
      marginTop: '2rem', 
      marginBottom: '0.75rem',
      color: 'var(--header-color, #ffffff)',
    },
    paragraph: {
      marginBottom: '1.6rem',
      fontSize: '1rem',
      color: 'var(--text-color, #e9ecef)',
    },
    listItem: {
      marginBottom: '0.6rem',
      paddingLeft: '0.5rem',
      color: 'var(--text-color, #e9ecef)', 
      lineHeight: '1.8',
    },
    codeBlock: {
      backgroundColor: 'var(--code-bg-color, #2c3e50)', 
      border: '1px solid var(--border-color, #495057)',
      borderRadius: '4px',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      overflowX: 'auto',
      whiteSpace: 'pre-wrap', 
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
      fontSize: '0.9rem',
      color: 'var(--code-text-color, #f8f9fa)',
      lineHeight: '1.6',
    },
    blockquote: {
        borderLeft: '5px solid var(--accent-color, #ffd700)',
        padding: '1.5rem 2rem',
        margin: '2.5rem 1rem',
        fontStyle: 'normal',
        color: 'var(--accent-text-color, #ffd700)',
        fontSize: '1.4rem',
        fontWeight: '600',
        backgroundColor: 'rgba(255, 215, 0, 0.08)',
        borderRadius: '4px',
    },
    takeawayBox: {
        backgroundColor: 'var(--highlight-bg-color, #2c3e50)', 
        border: '1px solid var(--accent-color, #495057)',
        borderRadius: '6px',
        padding: '1.5rem',
        marginTop: '2.5rem',
        color: 'var(--text-color, #ffffff)',
    },
    takeawayTitle: { 
      fontSize: '1.4rem',
      fontWeight: '600',
      marginTop: 0,
      marginBottom: '1rem',
      color: 'var(--highlight-title-color, #ffffff)',
    },
    scenarioContainer: {
      marginTop: '1.5rem',
      padding: '1.5rem',
      backgroundColor: 'var(--scenario-bg-color, #2c3e50)', 
      borderRadius: '6px',
      border: '1px solid var(--border-color, #495057)', 
    },
    scenarioInfo: {
        fontSize: '0.95rem',
        color: 'var(--text-color, #e9ecef)',
        marginBottom: '0.5rem',
    },
    streetContainer: {
        marginTop: '1rem',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px dashed var(--border-color, #495057)',
    },
    streetTitle: {
        fontSize: '1.15rem', 
        fontWeight: 'bold',
        color: 'var(--highlight-text-color, #ffffff)',
        marginBottom: '0.75rem',
        textTransform: 'uppercase', 
        letterSpacing: '0.5px',
    },
    boardContainer: {
        marginBottom: '0.75rem',
        color: 'var(--text-color, #e9ecef)',
    },
    actionLine: {
        marginLeft: '1rem', 
        marginBottom: '0.5rem',
        color: 'var(--text-color, #e9ecef)', 
    },
  };

  // --- NEW: Styles for Day Selector ---
  const daySelectorStyles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '2rem',
      paddingBottom: '1rem',
      borderBottom: '1px solid var(--border-color, #2c3e50)',
      gap: '0.5rem',
      flexWrap: 'wrap', // Allow wrapping on smaller screens
    },
    button: {
      padding: '0.5rem 1rem',
      border: '1px solid var(--subtext-color, #adb5bd)',
      borderRadius: '4px',
      backgroundColor: 'transparent',
      color: 'var(--subtext-color, #adb5bd)',
      cursor: 'pointer',
      fontSize: '0.9rem',
      transition: 'background-color 0.2s, color 0.2s',
    },
    buttonActive: {
      backgroundColor: 'var(--accent-color, #ffd700)', // Use accent color for active
      color: 'var(--panel-color, #1a2a4a)', // Dark text on active button
      borderColor: 'var(--accent-color, #ffd700)',
      fontWeight: 'bold',
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    unlockMessageContainer: {
        textAlign: 'center',
        padding: '3rem 1rem',
        fontSize: '1.2rem',
        color: 'var(--subtext-color, #adb5bd)',
    }
  };
  // --- END NEW Styles ---

  // Define CardDisplay props type
  interface CardDisplayProps {
    card: string;
  }
  const CardDisplay = ({ card }: CardDisplayProps): JSX.Element | null => {
    if (!card || card.length < 2) return null;
    const rank = card.slice(0, -1);
    const suit = card.slice(-1);
    const suitColor = (suit === '♥' || suit === '♦') ? '#dc3545' : '#212529'; 
    const suitSymbol = { h: '♥', d: '♦', c: '♣', s: '♠' }[suit.toLowerCase()] || suit;

    const cardStyle: React.CSSProperties = {
        display: 'inline-block',
        border: '1px solid #adb5bd',
        borderRadius: '4px',
        padding: '0.5em 0.4em',
        margin: '0.2em',
        minWidth: '2.5em',
        textAlign: 'center',
        fontWeight: 'bold',
        backgroundColor: '#ffffff',
        color: suitColor,
        fontSize: '1rem',
        lineHeight: '1',
        boxShadow: '1px 1px 3px rgba(0,0,0,0.1)',
    };

    return (
        <div style={cardStyle}>
            {rank}{suitSymbol}
        </div>
    );
  };

  // --- Render Logic --- 

  // Helper for Day Names
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const renderContent = () => {
    if (loading) {
        return <div style={styles.container}>Loading training content...</div>;
    }
    
    if (!isPremium) {
        return (
            <div style={styles.container}> 
                <h2 style={styles.sectionTitle}>Premium Content</h2>
                <p style={styles.paragraph}>{error || "Access denied. Premium membership required."}</p>
                 <a href="/subscribe" style={{marginTop: '1rem', display: 'inline-block'}}><button>Go Premium</button></a>
            </div>
        );
    }

    if (error) {
        return <div style={styles.container}>Error: {error}</div>;
    }
    
    // Handle future day selection
    if (unlockTimeRemaining) {
        return (
            <div style={daySelectorStyles.unlockMessageContainer}>
                <p>{unlockTimeRemaining}</p>
            </div>
        );
    }

    if (!blogData) {
        // This could happen if content wasn't found for a past/present day
        return <div style={styles.container}>No training content found for the selected day.</div>; 
    }

    // Render the actual blog data
    return (
        <>
            <h1 style={styles.mainTitle}>{blogData.title}</h1>
            <div style={styles.metaInfo}>
                <span>Difficulty: {blogData.difficulty} | </span>
                <span>Focus: {blogData.concept_focus}</span>
            </div>
            
            {blogData.narrative && (
                <>
                    <p style={styles.paragraph}><em>{blogData.narrative.intro}</em></p>
                    {
                      blogData.narrative.body?.split('\n\n').map((paragraph, index) => (
                        <p key={`body-para-${index}`} style={styles.paragraph}>{paragraph}</p>
                      ))
                    }
                    {blogData.narrative.mnemonic && <blockquote style={styles.blockquote}>{blogData.narrative.mnemonic}</blockquote>}
                    <p style={styles.paragraph}>{blogData.narrative.conclusion}</p>
                </>
            )}
            {blogData.scenario && (
                <>
                    <h2 style={styles.sectionTitle}>Scenario Breakdown</h2>
                    <div style={styles.scenarioContainer}>
                        <p style={styles.scenarioInfo}><strong>Game:</strong> {blogData.scenario.game_type}</p>
                        <p style={styles.scenarioInfo}><strong>Stacks:</strong> Hero({blogData.scenario.stacks?.hero}bb) vs Villain({blogData.scenario.stacks?.villain}bb)</p>
                        
                        <div style={styles.streetContainer}>
                            <h4 style={styles.streetTitle}>Preflop</h4>
                            <div style={styles.actionLine}>
                                <strong>Hero ({blogData.scenario.positions?.hero}):</strong> 
                                {blogData.scenario.preflop?.hero_hand?.map(card => <CardDisplay key={card} card={card} />)}
                            </div>
                            <div style={styles.actionLine}>
                               <strong>{blogData.scenario.positions?.villain}:</strong> {blogData.scenario.preflop?.villain_action}
                            </div>
                            <div style={styles.actionLine}>
                               <strong>{blogData.scenario.positions?.hero}:</strong> {blogData.scenario.preflop?.hero_action}
                            </div>
                        </div>

                        {blogData.scenario.flop && (blogData.scenario.flop.board?.length > 0 || blogData.scenario.flop.actions?.length > 0) && (
                          <div style={styles.streetContainer}>
                              <h4 style={styles.streetTitle}>Flop</h4>
                              <div style={styles.boardContainer}>
                                <strong>Board:</strong> 
                                {blogData.scenario.flop.board?.map(card => <CardDisplay key={card} card={card} />)}
                              </div>
                              {blogData.scenario.flop.actions?.map((action, index) => (
                                   <div key={`flop-action-${index}`} style={styles.actionLine}>
                                      <strong>{action.player}:</strong> {action.action}
                                  </div>
                              ))}
                          </div>
                        )}

                        {blogData.scenario.turn && (blogData.scenario.turn.card || blogData.scenario.turn.actions?.length > 0) && (
                          <div style={styles.streetContainer}>
                              <h4 style={styles.streetTitle}>Turn</h4>
                               <div style={styles.boardContainer}>
                                 <strong>Board:</strong> 
                                 {[...(blogData.scenario.flop?.board || []), blogData.scenario.turn.card].filter((c): c is string => !!c).map(card => <CardDisplay key={card} card={card} />)}
                              </div>
                              {blogData.scenario.turn.actions?.map((action, index) => (
                                   <div key={`turn-action-${index}`} style={styles.actionLine}>
                                      <strong>{action.player}:</strong> {action.action}
                                  </div>
                              ))}
                          </div>
                        )}

                        {blogData.scenario.river && (blogData.scenario.river.card || blogData.scenario.river.actions?.length > 0) && (
                          <div style={styles.streetContainer}>
                              <h4 style={styles.streetTitle}>River</h4>
                               <div style={styles.boardContainer}>
                                 <strong>Board:</strong> 
                                 {[...(blogData.scenario.flop?.board || []), blogData.scenario.turn?.card, blogData.scenario.river?.card].filter((c): c is string => !!c).map(card => <CardDisplay key={card} card={card} />)}
                              </div>
                              {blogData.scenario.river.actions?.map((action, index) => (
                                   <div key={`river-action-${index}`} style={styles.actionLine}>
                                      <strong>{action.player}:</strong> {action.action}
                                  </div>
                              ))}
                          </div>
                        )}
                    </div>
                </>
            )}
            {blogData.gto_reasoning && (
                <>
                    <h2 style={styles.sectionTitle}>GTO Reasoning</h2>
                    <ul style={{ paddingLeft: '20px' }}>
                        {blogData.gto_reasoning.points?.map((point, index) => (
                            <li key={index} style={styles.listItem}>{point}</li>
                        ))}
                    </ul>
                </>
            )}
            
            {blogData.math_simplified && (
                <>
                  <h3 style={styles.subSectionTitle}>Math Simplified</h3>
                  <p style={styles.paragraph}>Villain Bet: {blogData.math_simplified.villain_bet}</p>
                  <p style={styles.paragraph}>Fold Equity Needed: {blogData.math_simplified.fold_equity_needed}</p>
                  <p style={{...styles.paragraph, fontSize: '0.9rem', color: 'var(--subtext-color, #6c757d)'}}><em>Note: {blogData.math_simplified.note}</em></p> 
                </>
            )}

            {blogData.vs_population_advice && (
                <>
                  <h3 style={styles.subSectionTitle}>Population Adjustments</h3>
                  <p style={{...styles.paragraph, color: 'var(--text-color, #e9ecef)'}}><strong>Vs Tight Opponent:</strong> {blogData.vs_population_advice.tight_opponent}</p>
                  <p style={{...styles.paragraph, color: 'var(--text-color, #e9ecef)'}}><strong>Vs Bluffy Opponent:</strong> {blogData.vs_population_advice.bluffy_opponent}</p>
                </>
            )}

            {blogData.takeaway && (
              <div style={styles.takeawayBox}>
                  <h2 style={styles.takeawayTitle}>Key Takeaway</h2>
                  <p style={styles.paragraph}>{blogData.takeaway}</p>
              </div>
            )}
        </>
    );
  }

  return (
    <div style={styles.container}>
        {/* Day Selector UI */}
        <div style={daySelectorStyles.container}>
            {dayNames.map((dayName, index) => {
                // Simple check if day is in the future relative to current UTC day
                // let daysDifference = index - currentDayUTC;
                // if (daysDifference < 0) daysDifference += 7;
                // const isFuture = daysDifference > 0;
                
                return (
                    <button 
                        key={index} 
                        style={{
                            ...daySelectorStyles.button, 
                            ...(index === selectedDay ? daySelectorStyles.buttonActive : {}),
                            // ...(isFuture ? daySelectorStyles.buttonDisabled : {}) // Optional: Visually disable future buttons
                        }}
                        onClick={() => setSelectedDay(index)}
                        // disabled={isFuture} // Optional: Functionally disable future buttons
                    >
                        {dayName}
                    </button>
                );
            })}
        </div>

        {/* Render Content Area */}
        {renderContent()} 

    </div>
  );
}

export default LearnPage; 